import { AppLayout, Box, ColumnLayout, Container, Flashbar, Grid, Header, Icon, Link, Popover, SpaceBetween } from '@amzn/awsui-components-core';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Titles } from 'src/components/das-finsuite/das-finsuite-constants';
import { CostChangeIndicator } from 'src/components/generic-components/CostChangeIndicator';
import { ValueWithLabel } from 'src/components/generic-components/KeyValuePairs';
import LastRefreshedAt from 'src/components/generic-components/LastRefreshedAt';
import { LoadingSpinner } from 'src/components/generic-components/LoadingSpinner';
import { ResolutionBar } from 'src/components/generic-components/ProgressBar';
import { numberFormatter } from 'src/utilities/CommonUtilities';
import { ErrorContainer } from '../FinTechOpsCommonElements';
import { DASHBOARD_GRID_COLSPAN, FinTechOpsHref, FinTechOpsMetricsDescriptions } from '../FinTechOpsConstants';
import { useCostMetrics } from '../shared/context/CostMetricsProvider';
import { useDogma } from '../shared/context/DogmaProvider';
import { useNotification } from '../shared/context/NotificationProvider';
import { usePolicyEngine } from '../shared/context/PolicyEngineProvider';
import { useRiskRemediator } from '../shared/context/RiskRemediatorProvider';
import { useSas } from '../shared/context/SasProvider';
import { useSharedFilters } from '../shared/context/SharedFiltersProvider';
import { useTickets } from '../shared/context/TicketsProvider';
import { useUpgradeMedic } from '../shared/context/UpgradeMedicProvider';
import { useUserMetrics } from '../shared/context/UserMetricsProvider';
import { FinTechOpsDashboardHelpPanel } from './FinTechOpsDashBoardHelpPanel';
import { CardConfig, FiltersStatus, MetricItem } from './FinTechOpsDataInterfaces';
import { FinTechOpsSelectionFilters } from './FinTechOpsSelectionFilters';
import { MinutesToHoursConverter } from './FinTechOpsUtils';
import { OESideNavigation } from './OESideNavigation';
import styles from './styles.module.scss';

// --- Formatting helpers ---

const formatValue = (val: unknown, fallback = '-'): string => (val != null && val !== '' ? `${val}` : fallback);
const formatNumber = (val: number | undefined, fallback = '0'): string => (val != null && !Number.isNaN(val) ? numberFormatter(val) : fallback);
const parseCurrencyToNumber = (val: string | undefined): number => {
  if (!val) return 0;
  const num = Number(val.replace(/[^0-9.-]/g, ''));
  return Number.isNaN(num) ? 0 : num;
};

// --- Metric value renderer ---

const renderMetricValue = (item: MetricItem) => {
  switch (item.variant) {
    case 'large':
      return (
        <Box fontSize="heading-m" fontWeight="bold">
          {item.value}
        </Box>
      );
    case 'warning':
      return <Box color="text-status-warning">{item.value}</Box>;
    case 'error':
      return <Box color="text-status-error">{item.value}</Box>;
    default:
      return <>{item.value}</>;
  }
};

// --- Dashboard card ---

const DashboardCard: React.FC<{ config: CardConfig; navigate: ReturnType<typeof useNavigate> }> = ({ config, navigate }) => {
  const renderContent = () => {
    if (config.status === 'loading' || config.status === 'pending') return <LoadingSpinner />;
    if (config.status === 'error') return <ErrorContainer />;
    return (
      <>
        <ColumnLayout columns={2} variant="text-grid" minColumnWidth={120}>
          {config.headlineMetrics.map((item, i) => (
            <ValueWithLabel key={i} label={item.label} description={item.description}>
              {renderMetricValue(item)}
            </ValueWithLabel>
          ))}
        </ColumnLayout>
        {config.statusMetrics && config.statusMetrics.length > 0 && (
          <Box margin={{ top: 's' }}>
            <div className={styles.statusSection}>
              <ColumnLayout columns={config.statusMetrics.length} variant="text-grid" borders="vertical" minColumnWidth={100}>
                {config.statusMetrics.map((item, i) => (
                  <ValueWithLabel key={i} label={item.label} description={item.description}>
                    {renderMetricValue(item)}
                  </ValueWithLabel>
                ))}
              </ColumnLayout>
            </div>
          </Box>
        )}
        {config.resolution && (
          <ResolutionBar
            resolved={config.resolution.resolved}
            total={config.resolution.total}
            dynamic={config.resolution.dynamic ? config.resolution.dynamic : false}
            goodPercentage={config.resolution.goodPercentage ? config.resolution.goodPercentage : undefined}
            warningPercentage={config.resolution.warningPercentage ? config.resolution.warningPercentage : undefined}
          />
        )}
        {config.costChange && <CostChangeIndicator current={config.costChange.current} previous={config.costChange.previous} />}
        {config.filterNote && (
          <Box margin={{ top: 's' }} color="text-body-secondary" fontSize="body-s">
            <Icon name="status-info" size="small" /> {config.filterNote}
          </Box>
        )}
      </>
    );
  };

  return (
    <Container
      fitHeight
      header={
        <Header
          variant="h2"
          actions={
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              {config.lastUpdated && (
                <Popover
                  content={
                    <div className={styles.noBorder}>
                      <LastRefreshedAt lastRefreshedDateTime={config.lastUpdated} />
                    </div>
                  }
                  dismissButton={false}
                  size="small"
                  triggerType="custom"
                >
                  <Icon name="status-pending" size="normal" variant="subtle" />
                </Popover>
              )}
              <Link
                variant="primary"
                href={config.href}
                onFollow={(e) => {
                  e.preventDefault();
                  navigate(config.href);
                }}
              >
                View details
              </Link>
            </SpaceBetween>
          }
        >
          {config.title}
        </Header>
      }
    >
      {renderContent()}
    </Container>
  );
};

// --- Card data hook ---

const useCardConfigs = (): CardConfig[] => {
  const ticketsCtx = useTickets();
  const userMetricsCtx = useUserMetrics();
  const sasCtx = useSas();
  const policyCtx = usePolicyEngine();
  const dogmaCtx = useDogma();
  const costCtx = useCostMetrics();
  const rrCtx = useRiskRemediator();
  const umCtx = useUpgradeMedic();

  const tickets = ticketsCtx.finTechOpsTicketDashboard;
  const users = userMetricsCtx.finTechOpsUserMetricsDashBoard;
  const sas = sasCtx.finTechOpsSasDashBoard;
  const policy = policyCtx.finTechOpsPolicyEngineDashboardData;
  const dogma = dogmaCtx.finTechOpsDogmaDashboardData;
  const cost = costCtx.finTechOpsCostMetricsDashboardData;
  const rrDashboard = rrCtx.finTechOpsRiskRemediatorDashboardData;
  const umData = umCtx.upgradeMedicDashboardData;

  return [
    {
      title: Titles.TITLE_TICKETS,
      href: FinTechOpsHref.tickets,
      status: ticketsCtx.finTechOpsTicketDataStatus as FiltersStatus,
      lastUpdated: ticketsCtx.finTechOpsTicketsData?.metadata?.last_updated_at,
      headlineMetrics: [
        {
          label: 'Total Tickets',
          description: FinTechOpsMetricsDescriptions.totalTickets,
          value: formatNumber(tickets.totalTicketsCount),
          variant: 'large'
        },
        {
          label: 'Resolution Rate',
          description: FinTechOpsMetricsDescriptions.ticketsResolutionRate,
          value: tickets.resolutionRate != null ? `${formatNumber(tickets.resolutionRate)}%` : '0%',
          variant: 'large'
        }
      ],
      statusMetrics: [
        { label: 'Resolved', description: FinTechOpsMetricsDescriptions.ticketsSolvedTickets, value: formatNumber(tickets.solvedTicketCount) },
        {
          label: 'Open',
          description: FinTechOpsMetricsDescriptions.ticketsUnsolvedTickets,
          value: formatNumber(tickets.unSolvedTicketCount),
          variant: 'warning'
        },
        {
          label: 'Med. Resolution',
          description: FinTechOpsMetricsDescriptions.ticketsFirstResolutionTimeAvg,
          value: tickets.medianResolutionTimeInMinutes ? MinutesToHoursConverter(tickets.medianResolutionTimeInMinutes) : '0'
        }
      ],
      resolution: { resolved: tickets.solvedTicketCount || 0, total: tickets.totalTicketsCount || 0 }
    },
    {
      title: Titles.TITLE_USER_METRICS,
      href: FinTechOpsHref.userMetrics,
      status: userMetricsCtx.finTechOpsUserMetricsDataStatus as FiltersStatus,
      lastUpdated: userMetricsCtx.userMetricsWithUserData?.metadata?.last_updated_at,
      headlineMetrics: [
        {
          label: 'Total Applications',
          description: FinTechOpsMetricsDescriptions.userMetricsTotalApplications,
          value: formatNumber(Number(users.total_applications)),
          variant: 'large'
        },
        {
          label: 'Avg App Viewership',
          description: FinTechOpsMetricsDescriptions.userMetricsAverageViewership,
          value: formatNumber(Number(users.avg_view_per_application)),
          variant: 'large'
        },
        {
          label: 'Top App (Unique Users)',
          description: FinTechOpsMetricsDescriptions.currentMontnTopAppWithUniqueUsers,
          value: formatValue(users.app_most_unique_users)
        },
        {
          label: 'Most Viewed App',
          description: FinTechOpsMetricsDescriptions.currentMontnTopAppWithUsers,
          value: formatValue(users.most_viewed_application)
        }
      ]
    },
    {
      title: Titles.TITLE_SAS,
      href: FinTechOpsHref.sas,
      status: sasCtx.finTechOpsSasDataStatus as FiltersStatus,
      lastUpdated: sasCtx.sasData?.metadata?.last_updated_at,
      headlineMetrics: [
        {
          label: 'Total Risks',
          description: FinTechOpsMetricsDescriptions.sasTotalRisk,
          value: formatNumber(Number(sas.total_sas_risk)),
          variant: 'large'
        },
        {
          label: 'Resolution Rate',
          description: FinTechOpsMetricsDescriptions.sasResolutionRate,
          value: sas.resolution_rate != null ? `${formatNumber(Number(sas.resolution_rate))}%` : '0%',
          variant: 'large'
        }
      ],
      statusMetrics: [
        { label: 'Resolved', description: FinTechOpsMetricsDescriptions.sasSolved, value: formatNumber(Number(sas.solved)) },
        { label: 'Open', description: FinTechOpsMetricsDescriptions.sasUnsolved, value: formatNumber(Number(sas.unsolved)), variant: 'warning' },
        {
          label: 'Open w/ Recs',
          description: FinTechOpsMetricsDescriptions.sasOpenWithRecommendation,
          value: formatNumber(Number(sas.open_with_recommendation))
        }
      ],
      resolution: { resolved: Number(sas.solved) || 0, total: Number(sas.total_sas_risk) || 0 }
    },
    {
      title: Titles.TITLE_POLICY_ENGINE,
      href: FinTechOpsHref.policyEngine,
      status: policyCtx.finTechOpsPolicyEngineDataStatus as FiltersStatus,
      lastUpdated: policyCtx.finTechOpsPolicyEngineParsedData?.metadata?.last_updated_at,
      headlineMetrics: [
        {
          label: 'Total Risks',
          description: FinTechOpsMetricsDescriptions.policyEngineTotal,
          value: formatNumber(Number(policy.totalPolicyEngineRisks)),
          variant: 'large'
        },
        {
          label: 'Resolution Rate',
          description: FinTechOpsMetricsDescriptions.resolutionRate,
          value: policy.resolutionRate != null ? `${formatNumber(Number(policy.resolutionRate))}%` : '0%',
          variant: 'large'
        }
      ],
      statusMetrics: [
        { label: 'Resolved', description: FinTechOpsMetricsDescriptions.policyEngineSolved, value: formatNumber(Number(policy.solved)) },
        {
          label: 'Open',
          description: FinTechOpsMetricsDescriptions.policyEngineUnsolved,
          value: formatNumber(Number(policy.unsolved)),
          variant: 'warning'
        },
        {
          label: 'Open Critical',
          description: FinTechOpsMetricsDescriptions.totalOpenCriticalRisks,
          value: formatNumber(Number(policy.totalOpenCriticalRisks)),
          variant: 'error'
        }
      ],
      resolution: { resolved: Number(policy.solved) || 0, total: Number(policy.totalPolicyEngineRisks) || 0 }
    },
    {
      title: Titles.TITLE_DOGMA,
      href: FinTechOpsHref.dogma,
      status: dogmaCtx.finTechOpsDogmaDataStatus as FiltersStatus,
      lastUpdated: dogmaCtx.finTechOpsDogmaParsedData?.metadata?.last_updated_at,
      headlineMetrics: [
        {
          label: 'Total Risks',
          description: FinTechOpsMetricsDescriptions.dogmaTotalRisks,
          value: formatNumber(dogma.totalDogmaViolations),
          variant: 'large'
        },
        {
          label: 'Resolution Rate',
          description: FinTechOpsMetricsDescriptions.dogmaResolutionRate,
          value: dogma.resolutionRate != null ? `${formatNumber(Number(dogma.resolutionRate))}%` : '0%',
          variant: 'large'
        }
      ],
      statusMetrics: [
        { label: 'Resolved', description: FinTechOpsMetricsDescriptions.dogmaSolved, value: formatNumber(dogma.resolvedViolations) },
        { label: 'Open', description: FinTechOpsMetricsDescriptions.dogmaUnsolved, value: formatNumber(dogma.openViolations), variant: 'warning' },
        {
          label: 'Open High',
          description: FinTechOpsMetricsDescriptions.dogmaTotalOpenHighRisks,
          value: formatNumber(dogma.openWithHishRisksViolations),
          variant: 'error'
        }
      ],
      resolution: {
        resolved: Number(dogma.resolvedViolations) || 0,
        total: Number(dogma.totalDogmaViolations) || 0
      }
    },
    {
      title: Titles.TITLE_COST_METRICS,
      href: FinTechOpsHref.costMetrics,
      status: costCtx.finTechOpsCostMetricsDataStatus as FiltersStatus,
      lastUpdated: costCtx.finTechOpsCostMetricsParsedData?.metadata?.last_updated_at,
      headlineMetrics: [
        {
          label: 'Current Month Cost',
          description: FinTechOpsMetricsDescriptions.costMetricsTotalCurrentMonthCost,
          value: formatValue(cost.totalCurrentMonthCost, '$0'),
          variant: 'large'
        },
        {
          label: 'Previous Month Cost',
          description: FinTechOpsMetricsDescriptions.costMetricsTotalPreviousMonthCost,
          value: formatValue(cost.totalPreviousMonthCost, '$0'),
          variant: 'large'
        },
        {
          label: 'Avg Monthly Cost',
          description: FinTechOpsMetricsDescriptions.costMetricsAvgMonthlyCost,
          value: formatValue(cost.avgMonthlyCost, '$0'),
          variant: 'large'
        },
        {
          label: 'Top Product',
          description: FinTechOpsMetricsDescriptions.costMetricsTopProductContributingToCost,
          value: formatValue(cost.topProductContributingToCost, '-')
        }
      ],
      costChange: {
        current: parseCurrencyToNumber(cost.totalCurrentMonthCost),
        previous: parseCurrencyToNumber(cost.totalPreviousMonthCost)
      }
    },
    {
      title: Titles.TITLE_RISK_REMEDIATOR,
      href: FinTechOpsHref.riskRemediator,
      status: rrCtx.finTechOpsRiskRemediatorStatus as FiltersStatus,
      headlineMetrics: [
        {
          label: 'Total Risks',
          description: FinTechOpsMetricsDescriptions.riskRemediatorDashboardTotalRisks,
          value: formatNumber(rrDashboard.totalRisks),
          variant: 'large'
        },
        {
          label: 'Auto-Remediation Rate',
          description: FinTechOpsMetricsDescriptions.riskRemediatorDashboardAutoRemediationRate,
          value: `${formatNumber(rrDashboard.autoRemediationRate)}%`,
          variant: 'large'
        }
      ],
      statusMetrics: [
        {
          label: 'Auto-Remediated',
          description: FinTechOpsMetricsDescriptions.riskRemediatorDashboardAutoRemediated,
          value: formatNumber(rrDashboard.autoRemediated)
        },
        {
          label: 'Open',
          description: FinTechOpsMetricsDescriptions.riskRemediatorDashboardOpen,
          value: formatNumber(rrDashboard.openRisks),
          variant: 'warning'
        },
        {
          label: 'Dev Remediated',
          description: FinTechOpsMetricsDescriptions.riskRemediatorDashboardDevRemediated,
          value: formatNumber(rrDashboard.devRemediated)
        }
      ],
      resolution: { resolved: rrDashboard.remediated, total: rrDashboard.totalRisks }
    },
    {
      title: Titles.TITLE_UPGRADE_MEDIC,
      href: FinTechOpsHref.upgradeMedic,
      status: umCtx.upgradeMedicStatus as FiltersStatus,
      headlineMetrics: [
        {
          label: 'Total Lambdas',
          description: FinTechOpsMetricsDescriptions.upgradeMedicTotalLambdas,
          value: formatNumber(umData.totalLambdas),
          variant: 'large'
        },
        {
          label: 'EOL Runtimes',
          description: FinTechOpsMetricsDescriptions.upgradeMedicEolRuntimes,
          value: formatNumber(umData.eolLambdas),
          variant: 'large'
        }
      ],
      statusMetrics: [
        {
          label: 'Pipelines',
          description: FinTechOpsMetricsDescriptions.upgradeMedicTotalPipelines,
          value: formatNumber(umData.totalPipelines)
        },
        {
          label: 'EOL %',
          description: FinTechOpsMetricsDescriptions.upgradeMedicEolPercentage,
          value: `${formatNumber(umData.eolPercentage)}%`,
          variant: umData.eolPercentage > 20 ? 'error' : umData.eolPercentage > 10 ? 'warning' : 'plain'
        },
        {
          label: 'Job Executions',
          description: 'Total upgrade job executions tracked',
          value: formatNumber(umData.totalJobExecutions)
        }
      ]
    }
  ];
};

// --- Page component ---

export const FinTechOpsPage: React.FC = () => {
  const { flashbarItems } = useNotification();
  const sharedFilters = useSharedFilters();
  const navigate = useNavigate();
  const cards = useCardConfigs();

  return (
    <AppLayout
      contentType="dashboard"
      tools={<FinTechOpsDashboardHelpPanel />}
      navigation={<OESideNavigation />}
      toolsHide={true}
      notifications={<Flashbar items={flashbarItems} />}
      maxContentWidth={Number.MAX_VALUE}
      content={
        <SpaceBetween size="l">
          <FinTechOpsSelectionFilters />
          {sharedFilters.dashboardFilterDataCalculationStatus === 'loading' ? (
            <LoadingSpinner />
          ) : sharedFilters.dashboardFilterDataCalculationStatus === 'error' ? (
            <ErrorContainer />
          ) : sharedFilters.dashboardFilterDataCalculationStatus === 'finished' ? (
            <Grid gridDefinition={cards.map(() => ({ colspan: DASHBOARD_GRID_COLSPAN }))}>
              {cards.map((card) => (
                <DashboardCard key={card.href} config={card} navigate={navigate} />
              ))}
            </Grid>
          ) : null}
        </SpaceBetween>
      }
    />
  );
};
